// In src/pages/HelpPage.js

import React, { useState } from 'react'; // Removed unused 'useEffect'
import { Container, Typography, Box, TextField, Button, CircularProgress, Alert, FormControlLabel, Checkbox, Paper, Divider, List, ListItem, ListItemText } from '@mui/material'; // Removed unused 'ListItemIcon'
import FeedbackIcon from '@mui/icons-material/Feedback';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AdbIcon from '@mui/icons-material/Adb';

function HelpPage() {
  const { currentUser, userProfile } = useAuth();

  // Feedback Form States
  const [feedbackSubject, setFeedbackSubject] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState('');
  const [feedbackError, setFeedbackError] = useState('');

  // --- Feedback Form Logic ---
  const handleSubmitFeedback = async () => {
    setFeedbackSuccess('');
    setFeedbackError('');
    if (!feedbackSubject.trim() || !feedbackMessage.trim()) {
      setFeedbackError('Subject and message cannot be empty.');
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const feedbackCollectionRef = collection(db, 'churches', userProfile.churchId, 'feedback');
      
      const feedbackData = {
        subject: feedbackSubject.trim(),
        message: feedbackMessage.trim(),
        isAnonymous: isAnonymous,
        createdAt: serverTimestamp(),
      };

      if (!isAnonymous) {
        feedbackData.userId = currentUser.uid;
        feedbackData.userName = `${userProfile.firstName} ${userProfile.lastName}`;
        feedbackData.userEmail = currentUser.email;
      }

      await addDoc(feedbackCollectionRef, feedbackData);
      setFeedbackSuccess('Thank you for your feedback! We appreciate you helping us make this app better.');
      setFeedbackSubject('');
      setFeedbackMessage('');
      setIsAnonymous(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setFeedbackError(`Failed to submit feedback: ${error.message}`);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  if (!userProfile?.churchId) {
    return (
      <Container maxWidth="md">
        <Alert severity="info">
          Please ensure your user profile is associated with a church to access help features.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>Help & Support</Typography>

      <Paper elevation={3} sx={{ p: {xs: 2, md: 3}, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <DownloadIcon sx={{ mr: 1 }} />
          <Typography variant="h5" component="h2">How to Install This App</Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            For the best experience, you can install this app directly to your computer or phone home screen.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* --- Computer Instructions --- */}
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InstallDesktopIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">On a Computer (Chrome Browser)</Typography>
            </Box>
            <List dense>
                <ListItem>
                    <ListItemText primary="1. Look for the Install Icon in the address bar that looks like a computer screen with a downward arrow." />
                </ListItem>
                <ListItem>
                    <ListItemText primary="2. Click the icon, and when the prompt appears, click the blue 'Install' button." />
                </ListItem>
                <ListItem>
                    <ListItemText primary="3. The app will now be in your applications folder and on your desktop or taskbar." />
                </ListItem>
            </List>
        </Box>
        <Divider sx={{ my: 2 }} />

        {/* --- iPhone Instructions --- */}
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIphoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">On an iPhone or iPad</Typography>
            </Box>
            <List dense>
                <ListItem>
                    <ListItemText primary={<span>1. After logging in, tap the **Share** icon at the bottom of the Safari screen (a square with an arrow pointing up <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e0/Share_iOS_14.svg/20px-Share_iOS_14.svg.png" alt="iOS Share button" style={{ verticalAlign: 'middle', height: '1.2em' }} />).</span>} />
                </ListItem>
                <ListItem>
                    <ListItemText primary='2. Scroll down the list and tap on "Add to Home Screen".' />
                </ListItem>
                <ListItem>
                    <ListItemText primary='3. Confirm the name and tap "Add" in the top-right corner.' />
                </ListItem>
            </List>
        </Box>
        <Divider sx={{ my: 2 }} />

        {/* --- Android Instructions --- */}
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AdbIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">On an Android Phone</Typography>
            </Box>
            <List dense>
                <ListItem>
                    <ListItemText primary="1. After logging in, tap the three-dot menu icon (⋮) in the top-right corner of the Chrome browser." />
                </ListItem>
                <ListItem>
                    <ListItemText primary='2. From the menu that appears, select "Install app".' />
                </ListItem>
                <ListItem>
                    <ListItemText primary="3. Confirm the installation by tapping the 'Install' button in the pop-up." />
                </ListItem>
            </List>
        </Box>
      </Paper>
      
      <Paper elevation={3} sx={{ p: {xs: 2, md: 3}, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FeedbackIcon sx={{ mr: 1 }} />
          <Typography variant="h5" component="h2">Send App Feedback</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your feedback helps us improve! Please share any suggestions, issues, or comments you have.
        </Typography>

        {feedbackSuccess && <Alert severity="success" sx={{ mb: 2 }}>{feedbackSuccess}</Alert>}
        {feedbackError && <Alert severity="error" sx={{ mb: 2 }}>{feedbackError}</Alert>}

        <TextField
          fullWidth
          label="Subject"
          value={feedbackSubject}
          onChange={(e) => setFeedbackSubject(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Your Message"
          multiline
          rows={4}
          value={feedbackMessage}
          onChange={(e) => setFeedbackMessage(e.target.value)}
          margin="normal"
          required
        />
        <FormControlLabel
          control={<Checkbox checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} />}
          label="Submit Anonymously"
        />
        <Button
          variant="contained"
          startIcon={isSubmittingFeedback ? <CircularProgress size={20} color="inherit" /> : <FeedbackIcon />}
          onClick={handleSubmitFeedback}
          disabled={isSubmittingFeedback || !feedbackSubject.trim() || !feedbackMessage.trim()}
          sx={{ mt: 2 }}
        >
          Submit Feedback
        </Button>
      </Paper>

    </Container>
  );
}

export default HelpPage;